import React, { useEffect, useState } from 'react';
import { connect } from "react-redux";
import {
  MapContainer,
  TileLayer,
  Marker,
  GeoJSON,
  Popup,
  useMapEvents
} from "react-leaflet";
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from "leaflet";
import * as turf from '@turf/turf';
import provincesGeoJSON from '../../../../variables/phil_geo.json';
import { 
	getFarmLocations
} from "../../../../redux/dashboard/dashboardState";

import { displayFarmNameLocation } from "../../../../utils/index";

// Default marker fix (optional for proper icons)
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png"
});

const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();

  let size = "small";
  if (count > 10) size = "medium";
  if (count > 50) size = "large";

  return L.divIcon({
    html: `<div class="cluster-marker ${size}">${count}</div>`,
    className: "custom-cluster-icon",
    iconSize: L.point(40, 40, true),
  });
};

const PhilippinesMap = ({getMapData, recordList, requesting, success, error, ...props}) => {

  useEffect(() => {
    getMapData();
  }, [getMapData]);

  const normalize = (value) =>
    value?.toLowerCase().replace(/\s+/g, '');

  const matchFeatureByAddress = (farm, feature) => {
    return (
      normalize(farm.address?.province) === normalize(feature.properties.NAME_1) &&
      normalize(farm.address?.municipality) === normalize(feature.properties.NAME_2) &&
      normalize(farm.address?.barangay) === normalize(feature.properties.NAME_3)
    );
  };

  const resolveFarmPosition = (farm) => {
    //console.log(farm);
    if (farm.latitude && farm.longitude) {
      return [farm.latitude, farm.longitude];
    }

    const feature = provincesGeoJSON.features.find(f =>
      matchFeatureByAddress(farm, f)
    );

    if (!feature) return null;

    const centroid = turf.centroid(feature);
    const [lon, lat] = centroid.geometry.coordinates;

    return [lat, lon];
  };

  const renderPins = () => {
    return recordList
      ?.map((farm) => {
        const position = resolveFarmPosition(farm);

        //console.log(position);

        // No coordinates available at all
        if (!position) return null;

        return (
          <Marker
            key={farm.id}
            position={position}
          >
            <Popup>
              {displayFarmNameLocation(farm)}
              <div>
                {Object.entries(farm.animal_counts).map(([animal, count]) => (
                  <div key={animal}>
                    <strong>{animal}</strong>: {count}
                  </div>
                ))}
              </div>
            </Popup>
          </Marker>
        );
      })
      .filter(Boolean);
  };

  return (
    <MapContainer
      center={[12.8797, 121.7740]} 
      zoom={6}
      minZoom={6}
      maxZoom={17}
      style={{ height: "750px", width: "100%" }}
      scrollWheelZoom={true}
      maxBounds={[
        [4.5, 116],
        [21.5, 127]
      ]}
      maxBoundsViscosity={1.0}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
      />

      <MarkerClusterGroup
        iconCreateFunction={createClusterCustomIcon}
        spiderfyOnEveryZoom={true}
        showCoverageOnHover={false}
      >
        {renderPins()}
      </MarkerClusterGroup>

    </MapContainer>
  );
};

const mapStateToProps = (state) => ({
  requesting: state.dashboard.farmLocations?.requesting,
  success: state.dashboard.farmLocations?.success,
  error: state.dashboard.farmLocations?.error,
  recordList: state.dashboard.farmLocations?.items,
});

const mapDispatchToProps = (dispatch) => ({
  getMapData: (filters) => dispatch(getFarmLocations(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PhilippinesMap);